import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface BookingFormGuard {
  id: number;
  user_id?: number;
  text: string;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * CRUD client for the booking form information guards
 * (Back_End_Server /api/v1/booking-form-guards).
 *
 * Management calls are JWT-authed — the AuthInterceptor attaches the token
 * for every environment.baseUrl request. getPublicGuards is the unauthed
 * endpoint the booking form itself uses.
 */
@Injectable({ providedIn: 'root' })
export class BookingFormGuardsService {
  private readonly apiUrl = `${environment.baseUrl}/api/v1/booking-form-guards`;

  constructor(private http: HttpClient) {}

  /** All guards of the logged-in tenant, including disabled ones. */
  getGuards(): Observable<BookingFormGuard[]> {
    return this.http
      .get<{ message: string; data: BookingFormGuard[] }>(this.apiUrl)
      .pipe(map((res) => res.data ?? []));
  }

  /**
   * Enabled guards for a restaurant's booking form — public, no token.
   * When lang is given (browser language, e.g. 'de'), the backend translates
   * the guard texts server-side, falling back to the original wording.
   */
  getPublicGuards(userId: number, lang?: string): Observable<BookingFormGuard[]> {
    return this.http
      .get<{ message: string; data: BookingFormGuard[] }>(
        `${this.apiUrl}/public/${userId}`,
        { params: lang ? { lang } : {} }
      )
      .pipe(map((res) => res.data ?? []));
  }

  createGuard(guard: {
    text: string;
    enabled?: boolean;
    sort_order?: number;
  }): Observable<BookingFormGuard> {
    return this.http
      .post<{ message: string; data: BookingFormGuard }>(this.apiUrl, guard)
      .pipe(map((res) => res.data));
  }

  updateGuard(
    id: number,
    fields: Partial<Pick<BookingFormGuard, 'text' | 'enabled' | 'sort_order'>>
  ): Observable<BookingFormGuard> {
    return this.http
      .patch<{ message: string; data: BookingFormGuard }>(
        `${this.apiUrl}/${id}`,
        fields
      )
      .pipe(map((res) => res.data));
  }

  deleteGuard(id: number): Observable<void> {
    return this.http
      .delete<{ message: string }>(`${this.apiUrl}/${id}`)
      .pipe(map(() => undefined));
  }
}
